// LandingPage.jsx — Tailwind CSS refactor with improved UI
import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useCasesConfig from '../../constants/useCases';
import useCaseQuestionsConfig from '../../constants/useCaseQuestions';
import axios from "../../utils/axios";
import { aiResponses, getRandomResponse } from "../../constants/aiResponses";
import {
  validateEmail, validateLocation, validateOrgName, validateOutlets, validatePassword,
  validatePersonName, validatePhone, validateUsername
} from "../../constants/validations";
import { message } from "antd";
import { Send, Eye, Settings, Check, ArrowRight, X, EyeOff, ChevronDown, Zap, BarChart2, Bell, Monitor, TrendingUp, Plug } from "lucide-react";
import { useContext } from "react";
import { RootContext } from "../../context/RootContext";

/* ─── Sector data ─── */
const sectorCards = [
  { label: "QSR / Fast Food", emoji: "🍔", gradient: "from-red-500 to-orange-500", accent: "#ef4444", lightBg: "bg-red-50", border: "border-red-200", desc: "Staff productivity, queue time, food safety" },
  { label: "Manufacturing", emoji: "🏭", gradient: "from-orange-500 to-amber-500", accent: "#f97316", lightBg: "bg-orange-50", border: "border-orange-200", desc: "PPE compliance, line productivity, defects" },
  { label: "Retail", emoji: "🛍️", gradient: "from-violet-500 to-purple-600", accent: "#8b5cf6", lightBg: "bg-violet-50", border: "border-violet-200", desc: "Footfall analytics, queues, theft detection" },
];

/* ─── SectorPicker ─── */
function SectorPicker({ onSelect }) {
  const [hovered, setHovered] = useState(null);
  return (
    <div className="w-full">
      <div className="text-xs font-bold text-purple-600 tracking-widest uppercase mb-3">Choose your sector</div>
      <div className="grid grid-cols-1 gap-2">
        {sectorCards.map(card => (
          <div key={card.label}
            onClick={() => onSelect(card.label)}
            onMouseEnter={() => setHovered(card.label)}
            onMouseLeave={() => setHovered(null)}
            className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all duration-150 border ${hovered === card.label ? `${card.lightBg} ${card.border} shadow-md` : 'bg-white/80 border-purple-100'}`}>
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0 bg-gradient-to-br ${card.gradient} shadow-md`}>
              {card.emoji}
            </div>
            <div>
              <div className="text-sm font-bold text-slate-800">{card.label}</div>
              <div className="text-xs text-slate-500 leading-tight mt-0.5">{card.desc}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── UseCasePicker ─── */
function UseCasePicker({ sector, onConfirm }) {
  const config = useCasesConfig[sector] || {};
  const [selectedFunctions, setSelectedFunctions] = useState({});
  const [selected, setSelected] = useState({});
  const allItems = Object.entries(config).flatMap(([, items]) => items);
  const total = Object.values(selectedFunctions).flat().length;

  const toggleFunction = (useCaseId, fnName) => {
    setSelectedFunctions(prev => {
      const existing = prev[useCaseId] || [];
      const totalSelected = Object.values(prev).flat().length;
      const isAlreadySelected = existing.includes(fnName);
      if (!isAlreadySelected && totalSelected >= 4) { message.warning("Max 4 allowed"); return prev; }
      const updated = isAlreadySelected ? existing.filter(f => f !== fnName) : [...existing, fnName];
      return { ...prev, [useCaseId]: updated };
    });
  };

  const selectedUseCases = Object.keys(selectedFunctions).map(id => {
    const uc = allItems.find(u => u.id === id);
    return { id, title: uc?.title, functionalities: selectedFunctions[id] };
  }).filter(uc => uc.functionalities.length > 0);

  return (
    <div className="w-full">
      <div className="text-xs font-bold text-purple-600 tracking-widest uppercase mb-3">Select use cases for {sector}</div>
      {Object.entries(config).map(([groupName, items]) => (
        <div key={groupName} className="mb-3">
          <div className="text-xs font-bold text-purple-300 tracking-widest uppercase mb-2">{groupName}</div>
          <div className="flex flex-col gap-2">
            {items.map(uc => (
              <div key={uc.id} className="flex flex-col gap-2 p-3 rounded-xl bg-white/75 border border-purple-100">
                <div className="text-sm font-bold text-slate-800">{uc.title}</div>
                <div className="flex flex-col gap-1.5">
                  {uc.functionalities.map(fn => {
                    const isChecked = selectedFunctions[uc.id]?.includes(fn.name);
                    return (
                      <div key={fn.name}
                        onClick={() => toggleFunction(uc.id, fn.name)}
                        className={`flex items-center gap-2.5 p-2 rounded-lg cursor-pointer transition-all ${isChecked ? fn.isPremium ? 'bg-amber-50 border border-amber-300' : 'bg-purple-50 border border-purple-300' : 'hover:bg-slate-50 border border-transparent'}`}>
                        <div className={`w-4 h-4 rounded flex-shrink-0 flex items-center justify-center border-2 transition-all ${isChecked ? fn.isPremium ? 'bg-amber-400 border-amber-400' : 'bg-purple-600 border-purple-600' : 'bg-white border-purple-200'}`}>
                          {isChecked && <Check size={10} strokeWidth={3} className="text-white" />}
                        </div>
                        <span className={`text-xs font-semibold ${isChecked ? fn.isPremium ? 'text-amber-700' : 'text-purple-700' : 'text-slate-500'}`}>
                          {fn.name}{fn.isPremium && <span className="ml-1.5 text-amber-500">(Premium)</span>}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      <button
        onClick={() => total > 0 && onConfirm(selectedUseCases)}
        className={`mt-3 w-full py-3 px-4 rounded-xl font-bold text-sm transition-all ${total > 0 ? 'bg-gradient-to-r from-purple-600 to-violet-700 text-white shadow-lg shadow-purple-200 cursor-pointer' : 'bg-slate-100 text-slate-400 cursor-not-allowed'}`}>
        {total === 0 ? "Select at least one" : `Confirm ${total} Use Case${total > 1 ? "s" : ""} →`}
      </button>
    </div>
  );
}

/* ─── CameraSlider ─── */
function CameraSlider({ onConfirm }) {
  const [value, setValue] = useState(3);
  return (
    <div className="w-full">
      <div className="text-xs font-bold text-purple-600 tracking-widest uppercase mb-3">Cameras per outlet (max 15)</div>
      <div className="flex items-center gap-4 mb-2">
        <input type="range" min={1} max={15} value={value} onChange={e => setValue(Number(e.target.value))} className="flex-1 cursor-pointer accent-purple-600" />
        <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-extrabold text-xl flex-shrink-0 bg-gradient-to-br from-purple-600 to-violet-700 shadow-lg shadow-purple-200">
          {value}
        </div>
      </div>
      <div className="flex justify-between mb-4 pr-14">
        {[1, 4, 7, 10, 13, 15].map(n => (
          <span key={n} className={`text-xs font-bold ${value >= n ? 'text-purple-600' : 'text-slate-300'}`}>{n}</span>
        ))}
      </div>
      <button onClick={() => onConfirm(value)} className="w-full py-3 px-4 rounded-xl text-white font-bold text-sm cursor-pointer bg-gradient-to-r from-purple-600 to-violet-700 shadow-lg shadow-purple-200">
        Confirm {value} Camera{value > 1 ? "s" : ""} →
      </button>
    </div>
  );
}

/* ─── CameraOptions ─── */
function CameraOptions({ onSelect }) {
  const options = ["1–3 cameras", "4–6 cameras", "7–10 cameras", "10+ cameras", "No cameras yet"];
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(opt => (
        <button key={opt} onClick={() => onSelect(opt)} className="px-3.5 py-2 rounded-full text-sm font-semibold cursor-pointer transition-all hover:bg-purple-50 bg-white border border-purple-200 text-purple-700 hover:border-purple-400">
          {opt}
        </button>
      ))}
    </div>
  );
}

/* ─── StaffOptions ─── */
function StaffOptions({ onSelect }) {
  const options = ["1–5 staff", "6–10 staff", "11–20 staff", "20+ staff"];
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(opt => (
        <button key={opt} onClick={() => onSelect(opt)} className="px-3.5 py-2 rounded-full text-sm font-semibold cursor-pointer transition-all hover:bg-purple-50 bg-white border border-purple-200 text-purple-700 hover:border-purple-400">
          {opt}
        </button>
      ))}
    </div>
  );
}

/* ─── OTP Widget ─── */
function OtpWidget({ email, onVerified, onChangeEmail }) {
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleSend = async () => {
    setError(""); setSuccess(""); setLoading(true);
    try {
      await axios.post("/send-otp-register", { email });
      setOtpSent(true); setSuccess("A verification code has been sent to your email address.");
    } catch (err) { setError(err.response?.data?.message || "Unable to send verification code. Please try again."); }
    finally { setLoading(false); }
  };

  const handleVerify = async () => {
    if (!otp.trim()) { setError("Please enter the verification code to proceed."); return; }
    setError(""); setSuccess(""); setLoading(true);
    try {
      await axios.post("/verify-otp-register", { email, otp });
      setOtpVerified(true); setSuccess("✅ Your email address has been verified successfully.");
      setTimeout(() => onVerified(), 600);
    } catch (err) { setError(err.response?.data?.message || "The verification code entered is incorrect. Please try again."); }
    finally { setLoading(false); }
  };

  if (otpVerified) return (
    <div className="flex items-center gap-2 px-3.5 py-2.5 rounded-xl text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200">
      <Check size={14} strokeWidth={3} /> Email address verified.
    </div>
  );

  return (
    <div className="w-full space-y-2.5">
      <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-xl bg-purple-50 border border-purple-100">
        <div className="text-xs text-purple-500 font-medium min-w-0">
          <span className="text-purple-300">Sending to: </span>
          <span className="font-bold text-purple-700 truncate">{email}</span>
        </div>
        <button onClick={onChangeEmail} className="text-xs font-bold text-purple-400 hover:text-purple-600 cursor-pointer bg-transparent border-none underline underline-offset-2 flex-shrink-0">✏️ Change</button>
      </div>
      {!otpSent ? (
        <button onClick={handleSend} disabled={loading} className={`w-full py-2.5 px-4 rounded-xl text-white font-bold text-sm ${loading ? 'bg-purple-300 cursor-not-allowed' : 'bg-gradient-to-r from-purple-600 to-violet-700 cursor-pointer shadow-lg shadow-purple-200'}`}>
          {loading ? "Sending verification code..." : "Send Verification Code"}
        </button>
      ) : (
        <>
          <div className="text-xs text-emerald-600 font-semibold">✅ Verification code sent. Please check your inbox.</div>
          <div className="flex gap-2">
            <input type="text" value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="Enter 6-digit code"
              className="flex-1 px-3.5 py-2.5 rounded-xl text-sm font-mono font-bold text-slate-800 outline-none bg-white border border-purple-200 tracking-widest focus:border-purple-400" />
            <button onClick={handleVerify} disabled={loading} className={`px-4 py-2.5 rounded-xl text-white font-bold text-sm flex-shrink-0 ${loading ? 'bg-emerald-300 cursor-not-allowed' : 'bg-gradient-to-r from-emerald-500 to-green-600 cursor-pointer shadow-lg shadow-emerald-100'}`}>
              {loading ? "Verifying..." : "Verify"}
            </button>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => { setOtpSent(false); setOtp(""); setError(""); }} className="text-xs text-purple-400 underline cursor-pointer bg-transparent border-none hover:text-purple-600">Resend Code</button>
            <span className="text-purple-200 text-xs">·</span>
            <button onClick={onChangeEmail} className="text-xs text-purple-400 underline cursor-pointer bg-transparent border-none hover:text-purple-600">Update Email Address</button>
          </div>
        </>
      )}
      {error && <div className="text-xs text-red-500 font-semibold">⚠️ {error}</div>}
      {success && !otpVerified && <div className="text-xs text-emerald-600 font-semibold">{success}</div>}
    </div>
  );
}

/* ─── Password Widget ─── */
function PasswordWidget({ onConfirm }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [showConf, setShowConf] = useState(false);
  const [error, setError] = useState("");

  const handleConfirm = () => {
    setError("");
    const err = validatePassword(password);
    if (err) { setError(err); return; }
    if (password !== confirm) { setError("The passwords entered do not match. Please try again."); return; }
    onConfirm(password);
  };

  return (
    <div className="w-full space-y-2.5">
      <div className="space-y-2">
        <div className="relative">
          <input type={showPass ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} placeholder="Create a password (minimum 6 characters)"
            className="w-full px-3.5 py-2.5 rounded-xl text-sm font-medium text-slate-800 outline-none pr-10 bg-white border border-purple-200 focus:border-purple-400" />
          <button type="button" onClick={() => setShowPass(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-purple-300 hover:text-purple-500 cursor-pointer bg-transparent border-none">
            {showPass ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        </div>
        <div className="relative">
          <input type={showConf ? "text" : "password"} value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Confirm your password"
            className="w-full px-3.5 py-2.5 rounded-xl text-sm font-medium text-slate-800 outline-none pr-10 bg-white border border-purple-200 focus:border-purple-400" />
          <button type="button" onClick={() => setShowConf(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-purple-300 hover:text-purple-500 cursor-pointer bg-transparent border-none">
            {showConf ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        </div>
      </div>
      {error && <div className="text-xs text-red-500 font-semibold">⚠️ {error}</div>}
      {password.length >= 6 && confirm && password === confirm && (
        <div className="text-xs text-emerald-600 font-semibold flex items-center gap-1"><Check size={12} strokeWidth={3} /> Passwords match</div>
      )}
      <button onClick={handleConfirm} className="w-full py-2.5 px-4 rounded-xl text-white font-bold text-sm cursor-pointer bg-gradient-to-r from-purple-600 to-violet-700 shadow-lg shadow-purple-200">
        Set Password →
      </button>
    </div>
  );
}

/* ─── UsernameSuggestions ─── */
function UsernameSuggestions({ suggestions, onSelect }) {
  return (
    <div className="flex flex-col gap-2">
      {suggestions.map((username, index) => (
        <div key={username} onClick={() => onSelect(username)}
          className="flex items-center justify-between px-3 py-2 rounded-xl cursor-pointer bg-white hover:bg-purple-50 border border-purple-100 hover:border-purple-300 transition-all">
          <div className="flex items-center gap-2">
            {index === 0 && <span className="text-xs px-2 py-0.5 rounded-full font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">email</span>}
            <span className="text-sm font-semibold text-slate-800">{username}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─── Final Summary Card ─── */
function FinalSummaryCard({ formData, onRegister, isRegistering, registered }) {
  const camStr = String(formData.camerasPerOutlet || "");
  const camNums = camStr.match(/\d+/g);
  const camPerOutlet = camNums ? parseInt(camNums[camNums.length - 1]) : NaN;
  const totalCamera = isNaN(camPerOutlet) ? NaN : camPerOutlet * parseInt(formData.outlets);
  const labelMap = { sector: "Sector", useCases: "Use Cases", outlets: "Outlets", camerasPerOutlet: "Cams/Outlet", organizationName: "Organisation", personName: "Full Name", username: "Username", phone: "Phone", email: "Email", location: "Location" };

  if (registered) return (
    <div className="w-full rounded-2xl p-5 text-center bg-emerald-50 border border-emerald-200">
      <div className="text-3xl mb-3">🎉</div>
      <div className="font-extrabold text-emerald-700 text-base mb-1">Registration Complete</div>
      <div className="text-xs text-emerald-600">Your account has been created successfully. Please log in to access your dashboard.</div>
    </div>
  );

  return (
    <div className="w-full rounded-2xl p-4 bg-gradient-to-br from-purple-50 to-violet-50 border border-purple-200">
      <div className="text-xs font-bold text-purple-600 tracking-widest uppercase mb-3">✅ Registration Summary</div>
      <div className="mb-3 pb-3 border-b border-purple-100 border-dashed">
        <div className="text-xs font-bold text-purple-300 tracking-widest uppercase mb-2">Configuration</div>
        {["sector", "useCases", "outlets", "camerasPerOutlet"].map(key => {
          const val = formData[key];
          if (!val || (Array.isArray(val) && val.length === 0)) return null;
          return (
            <div key={key} className="flex gap-2 mb-1.5 items-start">
              <span className="text-xs font-bold text-purple-400 flex-shrink-0 pt-px w-28">{labelMap[key]}</span>
              <span className="text-sm text-slate-700 font-semibold leading-snug">{Array.isArray(val) ? val.map(v => (typeof v === "object" ? v.title : v)).join(", ") : val}</span>
            </div>
          );
        })}
        {!isNaN(totalCamera) && (
          <div className="flex gap-2 mb-1.5 items-start">
            <span className="text-xs font-bold text-purple-400 flex-shrink-0 pt-px w-28">Total Cameras</span>
            <span className="text-sm text-slate-700 font-semibold">{totalCamera}</span>
          </div>
        )}
      </div>
      <div className="mb-4">
        <div className="text-xs font-bold text-purple-300 tracking-widest uppercase mb-2">Account Details</div>
        {["organizationName", "personName", "username", "phone", "email", "location"].map(key => {
          const val = formData[key];
          if (!val) return null;
          return (
            <div key={key} className="flex gap-2 mb-1.5 items-start">
              <span className="text-xs font-bold text-purple-400 flex-shrink-0 pt-px w-28">{labelMap[key]}</span>
              <span className="text-sm text-slate-700 font-semibold leading-snug">{val}</span>
            </div>
          );
        })}
      </div>
      <button onClick={onRegister} disabled={isRegistering}
        className={`w-full py-3.5 px-5 rounded-2xl text-white font-extrabold text-base flex items-center justify-center gap-2.5 transition-all ${isRegistering ? 'bg-purple-300 cursor-not-allowed' : 'bg-gradient-to-r from-purple-600 to-violet-700 cursor-pointer shadow-lg shadow-purple-300 hover:shadow-xl hover:shadow-purple-300'}`}>
        {isRegistering ? "Setting up your account..." : <><span>Complete Registration</span><ArrowRight size={16} strokeWidth={2.5} /></>}
      </button>
    </div>
  );
}

/* ─── Registration steps config ─── */
const REG_STEPS = [
  { key: "organizationName", question: "**Let's set up your account.**\n\nPlease provide the name of your organisation.", placeholder: "Enter organisation name", validate: validateOrgName, widget: null },
  { key: "outlets", question: "How many **outlets or branches** does your organisation currently operate?", placeholder: "Enter number of outlets (e.g. 3)", validate: validateOutlets, widget: null },
  { key: "camerasPerOutlet", question: "How many **cameras per outlet** do you have, or plan to install?", widget: "camera-options" },
  { key: "personName", question: "Please provide your **full name** as it should appear on your account.", placeholder: "Enter your full name", validate: validatePersonName, widget: null },
  { key: "phone", question: "What is your **primary contact number**?\n\nPlease enter a valid 10-digit Indian mobile number.", placeholder: "Enter 10-digit mobile number", validate: validatePhone, inputType: "tel", widget: null },
  { key: "email", question: "Please enter your **work email address**.\n\nA verification code will be sent to confirm ownership.", placeholder: "Enter work email", validate: validateEmail, inputType: "email", widget: null },
  { key: "_otp", question: "A verification code has been dispatched to your email address.\n\n**Please enter it below to verify your account.**", widget: "otp-widget" },
  { key: "username", question: "Choose a **unique username** for your account.\n\nThis will be used to log in to the platform.", placeholder: "Enter a username (letters, numbers, _)", validate: validateUsername, widget: null },
  { key: "location", question: "What is the **primary operating location** of your organisation?\n\nThis helps us configure regional defaults.", placeholder: "Enter city or address", validate: validateLocation, widget: null },
  { key: "_password", question: "Please create a **secure password** for your account.\n\nUse at least 6 characters.", widget: "password-widget" },
];

/* ─── Inline Chat Section ─── */
function InlineChatSection({ navigate, onCollapse }) {
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const [inputVal, setInputVal] = useState("");
  const [messages, setMessages] = useState([]);
  const [chatStep, setChatStep] = useState(0);
  const [inputDisabled, setInputDisabled] = useState(false);
  const [inputPlaceholder, setInputPlaceholder] = useState("Type a message to begin...");
  const [errorMsg, setErrorMsg] = useState("");
  const [formData, setFormData] = useState({ sector: "", outlets: "", camerasPerOutlet: 1, useCases: [], organizationName: "", personName: "", username: "", phone: "", email: "", location: "", password: "" });
  const [dynamicQuestions, setDynamicQuestions] = useState([]);
  const [dynamicIndex, setDynamicIndex] = useState(0);
  const [regStepIndex, setRegStepIndex] = useState(0);
  const [phase, setPhase] = useState("config");
  const [isRegistering, setIsRegistering] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [waitingForUser, setWaitingForUser] = useState(true);
  const didInit = useRef(false);
  const messageQueueRef = useRef([]);
  const isStreamingRef = useRef(false);
  const { dispatch } = useContext(RootContext);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);
  useEffect(() => { if (textareaRef.current) textareaRef.current.focus(); }, [inputDisabled]);

  const processQueue = () => {
    if (isStreamingRef.current || messageQueueRef.current.length === 0) return;
    const { text, widget } = messageQueueRef.current.shift();
    isStreamingRef.current = true;
    const id = Date.now() + Math.random();
    const thinkDelay = 420 + Math.random() * 360;
    setMessages(prev => [...prev, { type: "bot", text, displayText: "", widget: null, pendingWidget: widget, streaming: true, thinking: true, id }]);
    setTimeout(() => {
      setMessages(prev => prev.map(m => m.id === id ? { ...m, thinking: false } : m));
      const chars = text.split("");
      let charIdx = 0;
      const streamNext = () => {
        if (charIdx >= chars.length) {
          setMessages(prev => prev.map(m => m.id === id ? { ...m, displayText: text, streaming: false, widget: m.pendingWidget } : m));
          isStreamingRef.current = false; processQueue(); return;
        }
        const chunk = Math.random() < 0.55 ? 1 : Math.random() < 0.7 ? 2 : 3;
        charIdx = Math.min(charIdx + chunk, chars.length);
        setMessages(prev => prev.map(m => m.id === id ? { ...m, displayText: text.slice(0, charIdx) } : m));
        const lastChar = chars[charIdx - 1];
        let delay;
        if (lastChar === "." || lastChar === "!" || lastChar === "?") delay = 80 + Math.random() * 120;
        else if (lastChar === "," || lastChar === ";") delay = 40 + Math.random() * 50;
        else if (lastChar === "\n") delay = 55 + Math.random() * 70;
        else if (lastChar === " ") delay = 18 + Math.random() * 22;
        else delay = 10 + Math.random() * 12;
        setTimeout(streamNext, delay);
      };
      streamNext();
    }, thinkDelay);
  };

  const addBotMessage = (text, widget = null) => { messageQueueRef.current.push({ text, widget }); processQueue(); };
  const addUserMessage = (text) => setMessages(prev => [...prev, { type: "user", text, id: Date.now() + Math.random() }]);
  const collapseWidget = (widgetName) => setMessages(prev => prev.map(m => m.widget === widgetName ? { ...m, widget: null, widgetDone: true } : m));

  const getUsernameSuggestions = async (email) => {
    try {
      const base = email.split("@")[0];
      const res = await axios.post("/suggest-username", { baseUsername: base });
      return res.data.suggestions;
    } catch (err) { return []; }
  };

  const triggerBotWelcome = () => {
    if (didInit.current) return;
    didInit.current = true;
    setTimeout(() => {
      addBotMessage("👋 Welcome to **OpsBot** — your AI-powered Operations Excellence platform.\n\nI will guide you through configuring a fully personalised Operational Control Room, tailored to your business environment.", null);
      setTimeout(() => {
        addBotMessage("To begin, we need to understand your operating context.\n\n**Industry Context**\nPlease select the sector that best describes your organisation.", "sector-picker");
        setChatStep(1); setInputDisabled(true); setInputPlaceholder("Please select your sector above to continue.");
      }, 600);
    }, 400);
  };

  const handleSectorSelect = async (label) => {
    collapseWidget("sector-picker"); addUserMessage(label);
    try {
      const res = await axios.post("/generate-opsbot-message", { type: "sector", sector: label });
      addBotMessage(res.data.message, null);
    } catch (err) { addBotMessage(getRandomResponse(aiResponses.sectorSelected, label), null); }
    setFormData(prev => ({ ...prev, sector: label }));
    setChatStep(2); setInputDisabled(true); setInputPlaceholder("Please select your use cases to continue.");
    setTimeout(() => { addBotMessage("**Use Case Configuration**\nPlease select the operational areas you would like OpsBot to monitor across your facilities.", "use-case-picker"); }, 400);
  };

  const handleCameraConfirm = (val) => {
    collapseWidget("camera-slider"); addUserMessage(`${val} camera${val > 1 ? "s" : ""} per outlet`);
    setFormData(prev => ({ ...prev, camerasPerOutlet: val })); setChatStep(5); setInputDisabled(true);
    setTimeout(() => addBotMessage("**Use Case Configuration**\nPlease select the operational areas you would like OpsBot to monitor.\n\nYou may select multiple use cases.", "use-case-picker"), 400);
  };

  const handleUseCaseConfirm = async (titles) => {
    collapseWidget("use-case-picker"); addUserMessage(titles.map(t => t.id.replace(/-/g, " ")).join(", "));
    try {
      const res = await axios.post("/generate-opsbot-message", { type: "usecases", sector: formData.sector, useCases: titles });
      addBotMessage(res.data.message, null);
    } catch (err) { addBotMessage(getRandomResponse(aiResponses.useCasesSelected, titles), null); }
    setFormData(prev => ({ ...prev, useCases: titles.map(t => ({ id: t.id.replace(/-/g, " "), title: t.title, functionalities: t.functionalities })) }));
    setInputPlaceholder("Type your response here...");
    const sectorConfig = useCasesConfig[formData.sector] || {};
    const allItems = Object.values(sectorConfig).flat();
    const selectedItems = allItems.filter(uc => titles.some(t => t.id === uc.id));
    const questions = [];
    selectedItems.forEach(uc => {
      const qConfig = useCaseQuestionsConfig[uc.id];
      if (qConfig) qConfig.forEach(q => { if (!questions.find(existing => existing.key === q.key)) questions.push(q); });
    });
    setDynamicQuestions(questions); setDynamicIndex(0); setChatStep(3);
    if (questions.length > 0) {
      setInputDisabled(questions[0].widget ? true : false);
      setTimeout(() => { addBotMessage(questions[0].question, questions[0].widget || null); }, 400);
    } else { startRegistrationPhase(); }
  };

  const processConfigStep = (value) => {
    const current = dynamicQuestions[dynamicIndex];
    if (!current) return;
    if (current.key === "outlets") { const err = validateOutlets(value); if (err) { setErrorMsg(err); return; } }
    collapseWidget(current.widget); addUserMessage(value);
    setFormData(prev => ({ ...prev, [current.key]: value }));
    const nextIndex = dynamicIndex + 1;
    if (nextIndex < dynamicQuestions.length) {
      const nextQ = dynamicQuestions[nextIndex]; setDynamicIndex(nextIndex);
      setInputDisabled(nextQ.widget ? true : false);
      setTimeout(() => { addBotMessage(nextQ.question, nextQ.widget || null); }, 400);
    } else { startRegistrationPhase(); }
  };

  const startRegistrationPhase = () => {
    setPhase("registration"); setRegStepIndex(0); setChatStep(10); setInputDisabled(false); setInputPlaceholder("Type your response here...");
    setTimeout(() => {
      addBotMessage(getRandomResponse(aiResponses.configComplete), null);
      setTimeout(() => {
        const firstStep = REG_STEPS[0];
        setInputDisabled(firstStep.widget ? true : false); setInputPlaceholder(firstStep.placeholder || "Type your response here...");
        addBotMessage(firstStep.question, firstStep.widget || null);
      }, 600);
    }, 500);
  };

  const advanceRegStep = (nextIdx, updatedFormData) => {
    if (nextIdx >= REG_STEPS.length) {
      setPhase("done"); setInputDisabled(true); setInputPlaceholder("Registration complete.");
      setTimeout(() => { addBotMessage("Your registration details have been compiled. Please review the summary below before finalising your account.", "final-summary"); }, 400);
      return;
    }
    const nextStep = REG_STEPS[nextIdx]; setRegStepIndex(nextIdx);
    if (nextStep.key === "username") { setInputDisabled(false); setInputPlaceholder("Select a suggestion or type a custom username."); return; }
    setInputDisabled(nextStep.widget ? true : false); setInputPlaceholder(nextStep.placeholder || "Type your response here...");
    setTimeout(() => { addBotMessage(nextStep.question, nextStep.widget || null); }, 400);
  };

  const processRegStep = async (value) => {
    const current = REG_STEPS[regStepIndex];
    if (!current) return;
    if (current.key === "username") {
      try {
        const res = await axios.post("/check-username", { username: value });
        if (!res.data.success) { setErrorMsg(res.data.message); addBotMessage("That username is already in use. Please choose a different one to continue.", null); return; }
        setErrorMsg(""); addUserMessage(value);
        const updatedFormData = { ...formData, username: value }; setFormData(updatedFormData);
        addBotMessage("✅ Username confirmed and reserved for your account.", null); advanceRegStep(regStepIndex + 1, updatedFormData); return;
      } catch (err) { setErrorMsg("We were unable to verify that username. Please try again."); return; }
    }
    if (current.validate) { const err = current.validate(value); if (err) { setErrorMsg(err); return; } }
    addUserMessage(value);
    const updatedFormData = { ...formData, [current.key]: value }; setFormData(updatedFormData);

    const acknowledgements = {
      organizationName: "Thank you — your organisation name has been recorded.",
      outlets: "Noted. We will configure the platform to support all your outlets.",
      camerasPerOutlet: "Camera count recorded. Processing capacity will be optimised accordingly.",
      personName: "Thank you for providing your details.",
      phone: "Your contact number has been saved. It will be used for critical operational alerts.",
      email: "Thank you. A verification code will be sent to this address shortly.",
      location: "Primary location saved. This will be used to apply regional compliance defaults.",
    };

    setErrorMsg(""); addBotMessage(acknowledgements[current.key] || "Your response has been recorded. Thank you.", null);
    if (current.widget) collapseWidget(current.widget);
    advanceRegStep(regStepIndex + 1, updatedFormData);
  };

  const handleOtpVerified = async () => {
    collapseWidget("otp-widget"); addUserMessage("Email address verified.");
    const suggestionsFromAPI = await getUsernameSuggestions(formData.email);
    const suggestions = [formData.email, ...suggestionsFromAPI];
    setFormData(prev => ({ ...prev, usernameSuggestions: suggestions }));
    setTimeout(() => { addBotMessage(`💡 **Username Selection**\n\nThe following usernames are available based on your email address. Please select one or enter a custom username below.`, "username-suggestions"); }, 400);
    advanceRegStep(regStepIndex + 1, formData);
  };

  const handleEmailChange = () => {
    collapseWidget("otp-widget"); setFormData(prev => ({ ...prev, email: "" }));
    const emailStepIdx = REG_STEPS.findIndex(s => s.key === "email"); setRegStepIndex(emailStepIdx);
    setInputDisabled(false); setInputPlaceholder("Enter your work email address.");
    addBotMessage("Understood. Please re-enter your **work email address** and we will send a new verification code.", null);
  };

  const handlePasswordConfirmed = (password) => {
    collapseWidget("password-widget"); setFormData(prev => ({ ...prev, password }));
    addUserMessage("🔒 Password set successfully."); advanceRegStep(regStepIndex + 1, { ...formData, password });
  };

  const handleFinalRegister = async () => {
    const camStr = String(formData.camerasPerOutlet || "");
    const camNums = camStr.match(/\d+/g);
    const camPerOutlet = camNums ? parseInt(camNums[camNums.length - 1]) : NaN;
    const outletCount = parseInt(formData.outlets);
    const totalCamera = isNaN(camPerOutlet) || isNaN(outletCount) ? NaN : camPerOutlet * outletCount;
    const required = { username: formData.username, password: formData.password, name: formData.personName, phone: formData.phone, email: formData.email, outlet: formData.outlets, organizationName: formData.organizationName, totalCamera: isNaN(totalCamera) ? "" : String(totalCamera), location: formData.location };
    for (const [key, val] of Object.entries(required)) {
      if (!val?.toString().trim()) { addBotMessage(`⚠️ It appears the **${key}** field is incomplete. Please revisit that step and provide the required information before proceeding.`, null); return; }
    }
    setIsRegistering(true);
    try {
      await axios.post("/register-user", { username: formData.username, password: formData.password, name: formData.personName, phone: formData.phone, email: formData.email, outlet: formData.outlets, organizationName: formData.organizationName, totalCamera, location: formData.location, ...(formData.sector && { sector: formData.sector }), ...(formData.useCases?.length && { useCases: formData.useCases }) });
      setRegistered(true);
      setTimeout(async () => {
        addBotMessage("✅ **Your account has been created successfully.** You are being redirected to your Operational Control Room.", null);
        try { localStorage.setItem("selectedUseCases", JSON.stringify(formData.useCases || [])); navigate("/demodashboard"); }
        catch (err) { navigate("/login"); }
      }, 800);
    } catch (err) { addBotMessage(`⚠️ We encountered an issue while creating your account: **${err.response?.data?.message || "An unexpected error occurred."}** Please review your details and try again.`, null); }
    finally { setIsRegistering(false); }
  };

  const handleSend = () => {
    if (!inputVal.trim() || inputDisabled) return;
    const val = inputVal.trim(); setInputVal(""); setErrorMsg("");
    if (waitingForUser) { addUserMessage(val); setWaitingForUser(false); triggerBotWelcome(); return; }
    if (phase === "config") processConfigStep(val);
    else if (phase === "registration") processRegStep(val);
  };

  const handleKeyDown = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } };

  let currentStep = chatStep >= 10 ? 4 : chatStep >= 3 ? 3 : chatStep;
  if (phase === "done") currentStep = 5;

  function formatMessage(text) {
    return text.split("\n").map((line, lineIdx, arr) => {
      const parts = line.split(/(\*\*.*?\*\*)/g).map((part, i) => {
        if (part.startsWith("**") && part.endsWith("**")) return <strong key={i}>{part.slice(2, -2)}</strong>;
        return part;
      });
      return <span key={lineIdx}>{parts}{lineIdx < arr.length - 1 && <br />}</span>;
    });
  }

  return (
    <div className="w-full h-full flex flex-col bg-slate-50">
      {/* Chat Header */}
      <div className="flex items-center justify-between px-4 md:px-6 py-3 border-b border-purple-100 bg-white flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center text-lg flex-shrink-0 bg-gradient-to-br from-purple-600 to-violet-700 shadow-lg shadow-purple-200">⚡</div>
          <div>
            <div className="font-extrabold text-sm text-slate-800 leading-tight">OpsBot — Operational Excellence AI</div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" style={{ boxShadow: "0 0 5px #22c55e" }} />
              <span className="text-xs text-emerald-500 font-semibold">Live · Enterprise Setup</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex flex-col items-end gap-1">
            <span className="text-xs text-purple-400 font-bold">{phase === "config" ? "Setup" : phase === "registration" ? "Registration" : "Complete"}</span>
            <div className="flex gap-1">
              {Array.from({ length: 5 }, (_, i) => (
                <div key={i} className="h-1 w-8 rounded-sm transition-all duration-300" style={{ background: currentStep > i ? "linear-gradient(90deg,#9333ea,#7c3aed)" : "rgba(209,213,219,0.6)" }} />
              ))}
            </div>
          </div>
          <button onClick={onCollapse} className="w-8 h-8 rounded-xl flex items-center justify-center cursor-pointer hover:bg-purple-50 border border-purple-100 text-purple-400 hover:text-purple-600 transition-colors bg-white">
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto py-4 min-h-0 scrollbar-thin scrollbar-thumb-purple-200 scrollbar-track-transparent">
        <div className="max-w-2xl mx-auto w-full px-4 flex flex-col gap-2.5">
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full py-16 gap-4">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl bg-gradient-to-br from-purple-600 to-violet-700 shadow-lg shadow-purple-200">⚡</div>
              <div className="text-center">
                <div className="font-extrabold text-slate-800 text-base mb-1">OpsBot is ready</div>
                <div className="text-sm text-slate-400 font-medium">Send a message to begin your enterprise setup.</div>
              </div>
              <div className="flex flex-wrap gap-2 justify-center mt-1">
                {["Hello!", "Good morning", "Let's get started"].map(suggestion => (
                  <button key={suggestion} onClick={() => { addUserMessage(suggestion); setWaitingForUser(false); triggerBotWelcome(); }}
                    className="px-3.5 py-1.5 rounded-full text-sm font-semibold cursor-pointer bg-white border border-purple-200 text-purple-600 hover:bg-purple-50 hover:border-purple-400 transition-all">
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((msg) => (
            <div key={msg.id} className={`flex items-start gap-2 ${msg.type === "user" ? "justify-end" : "justify-start"}`} style={{ animation: "msgIn 0.22s ease both" }}>
              {msg.type === "bot" && (
                <div className="w-7 h-7 rounded-lg flex-shrink-0 flex items-center justify-center text-xs mt-0.5 bg-gradient-to-br from-purple-600 to-violet-700 shadow-md shadow-purple-200">⚡</div>
              )}
              <div className={`min-w-0 ${msg.widget ? "flex-1 max-w-full" : "max-w-xs sm:max-w-sm md:max-w-md"}`}>
                {msg.type === "bot" && (
                  <div className="px-3.5 py-2.5 text-sm leading-relaxed font-medium text-slate-700 bg-white border border-slate-100 shadow-sm rounded-2xl rounded-tl-sm mb-2" style={{ minHeight: 36 }}>
                    {msg.thinking ? (
                      <span className="inline-flex items-center gap-1">
                        {[0, 1, 2].map(i => (<span key={i} className="inline-block w-2 h-2 rounded-full bg-purple-400" style={{ animation: `thinkDot 1.1s ease-in-out ${i * 0.18}s infinite` }} />))}
                      </span>
                    ) : (
                      <>
                        {formatMessage(msg.displayText)}
                        {msg.streaming && <span className="inline-block w-0.5 h-4 bg-purple-500 ml-0.5 align-bottom rounded" style={{ animation: "cursorBlink 0.75s step-end infinite" }} />}
                      </>
                    )}
                  </div>
                )}
                {msg.type === "user" && (
                  <div className="px-3.5 py-2.5 text-sm leading-snug font-medium text-white bg-gradient-to-br from-purple-600 to-violet-700 shadow-md shadow-purple-200 rounded-2xl rounded-tr-sm">
                    {msg.text}
                  </div>
                )}
                {/* Widgets */}
                {msg.widget === "sector-picker" && <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm"><SectorPicker onSelect={handleSectorSelect} /></div>}
                {msg.widget === "camera-slider" && <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm"><CameraSlider onConfirm={handleCameraConfirm} /></div>}
                {msg.widget === "camera-options" && <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm"><CameraOptions onSelect={(val) => phase === "registration" ? processRegStep(val) : processConfigStep(val)} /></div>}
                {msg.widget === "staff-options" && <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm"><StaffOptions onSelect={(val) => phase === "registration" ? processRegStep(val) : processConfigStep(val)} /></div>}
                {msg.widget === "use-case-picker" && <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm"><UseCasePicker sector={formData.sector} onConfirm={handleUseCaseConfirm} /></div>}
                {msg.widget === "otp-widget" && <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm"><OtpWidget email={formData.email} onVerified={handleOtpVerified} onChangeEmail={handleEmailChange} /></div>}
                {msg.widget === "password-widget" && <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm"><PasswordWidget onConfirm={handlePasswordConfirmed} /></div>}
                {msg.widget === "username-suggestions" && <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm"><UsernameSuggestions suggestions={formData.usernameSuggestions || []} onSelect={(val) => processRegStep(val)} /></div>}
                {msg.widget === "final-summary" && <FinalSummaryCard formData={formData} onRegister={handleFinalRegister} isRegistering={isRegistering} registered={registered} />}
                {msg.widgetDone && (
                  <div className="inline-flex items-center gap-1 mt-1.5 text-xs text-emerald-600 font-bold bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                    <Check size={11} strokeWidth={3} /> Confirmed
                  </div>
                )}
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Bar */}
      <div className="border-t border-slate-100 bg-white flex-shrink-0 px-3 md:px-6 pt-2.5 pb-4">
        <div className="max-w-2xl mx-auto w-full">
          {errorMsg && (
            <div className="mb-2 px-3 py-2 rounded-xl text-red-600 text-xs font-semibold flex items-center gap-1.5 bg-red-50 border border-red-200">
              ⚠️ {errorMsg}
            </div>
          )}
          <div className={`flex items-center gap-2 px-3.5 py-2 rounded-2xl transition-all border ${inputDisabled ? 'bg-slate-50 border-slate-100' : errorMsg ? 'bg-white border-red-300' : 'bg-white border-purple-200 shadow-sm'}`}>
            <input
              ref={textareaRef}
              value={inputVal}
              onChange={e => { setInputVal(e.target.value); if (errorMsg) setErrorMsg(""); }}
              onKeyDown={handleKeyDown}
              placeholder={inputPlaceholder}
              disabled={inputDisabled}
              type={phase === "registration" && REG_STEPS[regStepIndex]?.inputType ? REG_STEPS[regStepIndex].inputType : "text"}
              className={`flex-1 bg-transparent border-none outline-none text-sm ${inputDisabled ? 'text-slate-400 cursor-not-allowed' : 'text-slate-800'}`}
            />
            <button onClick={handleSend} className={`w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center transition-all border-none ${(inputVal.trim() && !inputDisabled) ? 'bg-gradient-to-br from-purple-600 to-violet-700 text-white shadow-md shadow-purple-200 cursor-pointer' : 'bg-slate-100 text-slate-400 cursor-not-allowed'}`}>
              <Send size={16} />
            </button>
          </div>
          <div className="text-center mt-1.5">
            <span className="text-xs text-slate-400 font-medium">Powered by Citriot AI · Press Enter to send</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════
   LANDING PAGE SECTIONS — Clean Tailwind UI
════════════════════════════════════════ */

/* ── Ticker ── */
function LandingTicker() {
  const items = ["Retail Analytics", "Queue Management", "PPE Compliance", "Theft Detection", "Food Safety Monitoring", "Workforce Intelligence", "Planogram Compliance", "Fall Detection", "Anomaly Alerting", "Manufacturing QA"];
  return (
    <div className="bg-slate-900 py-3 overflow-hidden border-y border-slate-800">
      <div className="inline-flex" style={{ animation: "lpTicker 30s linear infinite" }}>
        {[...items, ...items].map((t, i) => (
          <span key={i} className="px-7 text-xs font-bold tracking-widest uppercase text-slate-400 inline-flex items-center gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block flex-shrink-0" />{t}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ── Camera Feed Scenes (pure CSS) ── */
function CamScene({ type }) {
  if (type === "retail") return (
    <div className="absolute inset-0 bg-slate-900">
      <div className="absolute bottom-1/3 left-1/4 right-1/4 h-px bg-white/10" />
      <div className="absolute top-1/3 left-1/3 w-16 h-16 rounded-full" style={{ background: "radial-gradient(circle, rgba(255,100,0,0.4), transparent 70%)", animation: "camHeat 3s ease-in-out infinite" }} />
      <div className="absolute bottom-1/3 w-3 h-8 bg-white/20 rounded-t" style={{ animation: "camWalk 6s ease-in-out infinite" }} />
    </div>
  );
  if (type === "queue") return (
    <div className="absolute inset-0 bg-slate-900/95">
      <div className="absolute top-1/4 left-3 right-3 h-1 bg-white/10 rounded overflow-hidden">
        <div className="h-full bg-red-500 rounded" style={{ width: "75%", animation: "camQueue 4s ease-in-out infinite" }} />
      </div>
      <div className="absolute bottom-1/4 left-1/2 -translate-x-1/2 flex gap-1.5">
        {[0,1,2,3,4].map(i => <div key={i} className="w-2 h-6 bg-white/20 rounded-t" style={{ animation: `camBob 2s ease-in-out ${i*0.15}s infinite` }} />)}
      </div>
    </div>
  );
  if (type === "ppe") return (
    <div className="absolute inset-0" style={{ background: "#0d1a12", backgroundImage: "repeating-linear-gradient(90deg, rgba(16,185,129,0.04) 0, rgba(16,185,129,0.04) 1px, transparent 1px, transparent 36px), repeating-linear-gradient(0deg, rgba(16,185,129,0.04) 0, rgba(16,185,129,0.04) 1px, transparent 1px, transparent 36px)" }}>
      <div className="absolute top-1/4 left-1/6 w-1/3 h-2/5 border border-emerald-500 rounded" style={{ animation: "camBox 4s ease-in-out infinite" }}>
        <span className="absolute -top-4 left-0 text-xs text-emerald-400 font-bold">Person 94%</span>
      </div>
      <div className="absolute top-1/3 right-1/6 w-1/4 h-1/3 border border-red-500 rounded" style={{ animation: "camBox 4s ease-in-out 1s infinite" }}>
        <span className="absolute -top-4 left-0 text-xs text-red-400 font-bold">No PPE 87%</span>
      </div>
    </div>
  );
  if (type === "mfg") return (
    <div className="absolute inset-0 bg-slate-900">
      <div className="absolute bottom-1/3 left-0 right-0 h-4 bg-white/10" />
      {[0,1,2].map(i => <div key={i} className="absolute bottom-1/3 w-4 h-3 bg-amber-300/30 rounded" style={{ animation: `camConvey 4s linear ${-i*1.33}s infinite` }} />)}
      <div className="absolute top-1/6 left-1/5 border border-emerald-500 w-6 h-8 rounded" />
      <span className="absolute top-4 left-16 text-xs text-emerald-400 font-bold">PPE ✓</span>
    </div>
  );
  if (type === "zone") return (
    <div className="absolute inset-0 bg-slate-950">
      <div className="absolute top-1/6 left-2 w-5/12 h-3/5 border border-dashed border-violet-500/50 rounded">
        <span className="absolute top-1 left-1.5 text-xs text-violet-400 font-bold uppercase">Zone A</span>
      </div>
      <div className="absolute top-1/4 right-1 w-1/3 h-2/5 border border-dashed border-red-400/40 rounded">
        <span className="absolute top-1 left-1.5 text-xs text-red-400 font-bold uppercase">Restricted</span>
      </div>
      <div className="absolute w-2 h-2 rounded-full bg-violet-500" style={{ animation: "camZone 5s ease-in-out infinite" }} />
    </div>
  );
  return null;
}

function CamFeed({ id, type, alert, alertBg, large }) {
  return (
    <div className={`bg-slate-800 rounded-xl relative overflow-hidden border border-white/5 ${large ? "col-span-2" : ""}`} style={{ aspectRatio: "16/9" }}>
      <CamScene type={type} />
      <div className="absolute inset-0 pointer-events-none z-10" style={{ background: "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.02) 2px, rgba(0,0,0,0.02) 4px)" }} />
      <div className="absolute top-0 left-0 right-0 p-2 flex justify-between items-center z-20" style={{ background: "linear-gradient(to bottom, rgba(0,0,0,0.6), transparent)" }}>
        <span className="text-xs text-white/60 font-medium">{id}</span>
        <span className="flex items-center gap-1 text-xs text-red-400 font-bold">
          <span className="w-1.5 h-1.5 rounded-full bg-red-400 inline-block" style={{ animation: "camBlink 1.5s infinite" }} />REC
        </span>
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-2 z-20" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.7), transparent)" }}>
        <span className={`inline-flex items-center gap-1.5 ${alertBg} rounded-full px-2.5 py-1 text-xs font-medium text-white`}>
          <span className="w-1.5 h-1.5 rounded-full bg-white/70 inline-block" />{alert}
        </span>
      </div>
    </div>
  );
}

function CameraGrid() {
  return (
    <section className="bg-slate-950 py-20 px-6 md:px-12 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 65% 40%, rgba(99,102,241,0.1), transparent 55%)" }} />
      <div className="max-w-6xl mx-auto relative">
        <div className="text-xs font-bold tracking-widest uppercase text-emerald-400 mb-4">Live Intelligence Layer</div>
        <h2 className="text-3xl md:text-5xl font-extrabold text-white tracking-tight leading-tight mb-12 max-w-lg">
          Your cameras. <em className="text-indigo-400 not-italic">Supercharged</em> with AI.
        </h2>
        <div className="grid grid-cols-3 gap-2.5">
          <CamFeed id="CAM-01 · Retail Floor A" type="retail" alert="Footfall: 47 · Peak zone" alertBg="bg-emerald-600/90" large />
          <CamFeed id="CAM-02 · Checkout" type="queue" alert="Queue >3 min" alertBg="bg-red-600/85" />
          <CamFeed id="CAM-03 · Warehouse" type="ppe" alert="PPE violation" alertBg="bg-red-600/85" />
          <CamFeed id="CAM-04 · Production" type="mfg" alert="94% throughput" alertBg="bg-emerald-600/90" large />
          <CamFeed id="CAM-05 · Zone A" type="zone" alert="Motion tracked" alertBg="bg-violet-600/90" />
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    { n: "01", icon: "📷", title: "Connect Your Cameras", desc: "Works with existing CCTV — Hikvision, Axis, Dahua and 200+ brands. No new hardware needed." },
    { n: "02", icon: "🧠", title: "AI Analyses Frames", desc: "Vision models run at 25+ FPS per camera, detecting people, objects, actions and anomalies instantly." },
    { n: "03", icon: "⚡", title: "Instant Smart Alerts", desc: "Get notified on Slack, WhatsApp or Teams the second something needs your attention. Zero noise." },
    { n: "04", icon: "📊", title: "Auto Daily Digest", desc: "8 hours of footage compressed into a 2-minute PDF briefing. Auto-generated. Always shareable." },
  ];
  return (
    <section className="bg-slate-50 py-20 px-6 md:px-12">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <div className="text-xs font-bold tracking-widest uppercase text-indigo-500 mb-4">How It Works</div>
          <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 tracking-tight leading-tight">
            From cameras to insights in <span className="text-indigo-600">under 10 minutes</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {steps.map((s, i) => (
            <div key={s.n} className="bg-white rounded-2xl p-6 border border-slate-200 relative hover:shadow-lg hover:shadow-indigo-50 transition-shadow">
              <div className="absolute top-5 right-5 text-5xl font-extrabold text-slate-100 leading-none select-none">{s.n}</div>
              <div className="text-3xl mb-5">{s.icon}</div>
              <h3 className="text-base font-bold text-slate-900 mb-2.5">{s.title}</h3>
              <p className="text-sm text-slate-500 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Features() {
  const feats = [
    { icon: <Zap size={22} />, color: "text-indigo-500", bg: "bg-indigo-50", title: "Real-time Detection", desc: "AI processes feeds live, flagging incidents and anomalies the moment they happen — not hours later.", tags: ["Sub-second", "24/7"] },
    { icon: <BarChart2 size={22} />, color: "text-emerald-500", bg: "bg-emerald-50", title: "Auto Video Summaries", desc: "8 hours of footage → 2-minute briefing. Shareable PDF reports generated automatically every day.", tags: ["Daily digest", "PDF export"] },
    { icon: <Bell size={22} />, color: "text-amber-500", bg: "bg-amber-50", title: "Smart Alerts", desc: "Notifications on Slack, WhatsApp or Teams instantly when something needs your attention.", tags: ["Slack", "WhatsApp", "Teams"] },
    { icon: <Monitor size={22} />, color: "text-violet-500", bg: "bg-violet-50", title: "Multi-site Control Room", desc: "All locations in one dashboard. Drill into any site, camera, or time window in seconds.", tags: ["Unlimited sites", "Unified"] },
    { icon: <TrendingUp size={22} />, color: "text-rose-500", bg: "bg-rose-50", title: "ROI Tracking", desc: "Live dashboard showing hours saved, incidents prevented, and cost savings — auto-calculated.", tags: ["Cost savings", "Live"] },
    { icon: <Plug size={22} />, color: "text-cyan-500", bg: "bg-cyan-50", title: "Works with Your Cameras", desc: "No rip-and-replace. Connects to existing infrastructure — Hikvision, Axis, Dahua & 200+ others.", tags: ["RTSP", "200+ brands"] },
  ];
  return (
    <section className="py-20 px-6 md:px-12 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="mb-14">
          <div className="text-xs font-bold tracking-widest uppercase text-indigo-500 mb-4">Features</div>
          <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 tracking-tight leading-tight max-w-xl">
            Intelligence at every layer of your operations
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 rounded-2xl overflow-hidden border border-slate-200">
          {feats.map(f => (
            <div key={f.title} className="bg-white p-7 hover:bg-slate-50 transition-colors">
              <div className={`w-11 h-11 rounded-xl ${f.bg} flex items-center justify-center ${f.color} mb-5`}>{f.icon}</div>
              <h3 className="text-base font-bold text-slate-900 mb-2.5">{f.title}</h3>
              <p className="text-sm text-slate-500 leading-relaxed mb-4">{f.desc}</p>
              <div className="flex flex-wrap gap-1.5">
                {f.tags.map(t => <span key={t} className={`text-xs font-medium px-2.5 py-1 rounded-full ${f.bg} ${f.color}`}>{t}</span>)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ModelTraining() {
  const stages = [
    { n: "1", title: "Data Ingestion", desc: "Camera feeds stream via RTSP. Frames processed securely on-premise or cloud.", color: "bg-indigo-900/40 text-indigo-300 border-indigo-700" },
    { n: "2", title: "Pre-trained Backbone", desc: "State-of-the-art ViT and YOLO-based detectors pre-trained on 100M+ annotated frames.", color: "bg-violet-900/40 text-violet-300 border-violet-700" },
    { n: "3", title: "Domain Fine-tuning", desc: "Models fine-tuned on your industry: retail layouts, warehouse, QSR kitchens.", color: "bg-emerald-900/40 text-emerald-300 border-emerald-700" },
    { n: "4", title: "Continuous Learning", desc: "Every alert you accept or dismiss retrains the model weekly. Accuracy improves automatically.", color: "bg-amber-900/40 text-amber-300 border-amber-700" },
    { n: "5", title: "Real-time Inference", desc: "Edge-optimized models run at 25+ FPS per camera with sub-500ms alert latency.", color: "bg-rose-900/40 text-rose-300 border-rose-700" },
  ];
  return (
    <section className="py-20 px-6 md:px-12" style={{ background: "linear-gradient(160deg, #0f172a 0%, #1e1b4b 100%)" }}>
      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
        <div>
          <div className="text-xs font-bold tracking-widest uppercase text-emerald-400 mb-4">Under The Hood</div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight leading-tight mb-4">
            How we train <br /><span className="text-indigo-400">your AI model</span>
          </h2>
          <p className="text-slate-400 leading-relaxed mb-10 text-sm">
            Every CogniVision deployment is fine-tuned to your specific environment — your cameras, your layout, your operations.
          </p>
          <div className="flex gap-4">
            {[{ n: "100M+", l: "Training frames" }, { n: "98%", l: "Fine-tune accuracy" }].map(s => (
              <div key={s.l} className="bg-white/5 border border-white/10 rounded-2xl px-5 py-4">
                <div className="text-2xl font-extrabold text-white mb-1">{s.n}</div>
                <div className="text-xs text-slate-400">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-2.5">
          {stages.map(s => (
            <div key={s.n} className="flex gap-3 items-start bg-white/4 border border-white/8 rounded-2xl p-4 hover:bg-white/7 transition-colors">
              <div className={`w-9 h-9 rounded-lg border flex items-center justify-center text-sm font-extrabold flex-shrink-0 ${s.color}`}>{s.n}</div>
              <div>
                <div className="font-bold text-white text-sm mb-1">{s.title}</div>
                <div className="text-xs text-slate-400 leading-relaxed">{s.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Sectors() {
  const sectors = [
    { emoji: "🛍️", name: "Retail", desc: "Turn every camera into a loss prevention officer, store analyst, and planogram auditor simultaneously.", modules: ["Footfall & heatmapping", "Theft & loss prevention", "Queue management", "Planogram compliance", "POS reconciliation"], dot: "bg-violet-500" },
    { emoji: "🍔", name: "QSR / Food Service", desc: "Keep food safety auditors happy, queues short, and staff productive without adding headcount.", modules: ["Food safety & hygiene", "Customer wait time", "Staff PPE compliance", "Inventory & wastage", "Shift scheduling insight"], dot: "bg-red-500" },
    { emoji: "🏭", name: "Manufacturing", desc: "Zero-miss defect detection, PPE compliance, and line productivity from existing cameras.", modules: ["Line productivity", "Visual defect detection", "PPE & safety compliance", "Equipment idle detection", "SOP deviation alerts"], dot: "bg-orange-500" },
  ];
  return (
    <section className="py-20 px-6 md:px-12 bg-slate-50">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <div className="text-xs font-bold tracking-widest uppercase text-indigo-500 mb-4">Built For Your Industry</div>
          <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 tracking-tight leading-tight">
            Deep intelligence for every vertical
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {sectors.map(s => (
            <div key={s.name} className="bg-white border border-slate-200 rounded-2xl p-7 hover:shadow-lg hover:shadow-slate-100 transition-shadow">
              <span className="text-4xl block mb-5">{s.emoji}</span>
              <h3 className="text-xl font-extrabold text-slate-900 mb-2.5 tracking-tight">{s.name}</h3>
              <p className="text-sm text-slate-500 leading-relaxed mb-6">{s.desc}</p>
              <div className="flex flex-col gap-2">
                {s.modules.map(m => (
                  <div key={m} className="flex items-center gap-2.5 text-sm text-slate-600 font-medium">
                    <span className={`w-1.5 h-1.5 rounded-full ${s.dot} flex-shrink-0`} />{m}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-slate-950 px-6 md:px-12 pt-14 pb-7 border-t border-white/5">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start gap-8 mb-12">
          <div>
            <div className="text-2xl font-extrabold text-white tracking-tight mb-2">Cogni<span className="text-purple-400">Vision</span></div>
            <div className="text-sm text-slate-400 max-w-xs leading-relaxed">AI-powered video intelligence for operations teams that can't afford to miss anything.</div>
          </div>
          <div className="flex gap-12 md:gap-16 flex-wrap">
            {[{ title: "Product", links: ["Features", "Sectors", "Integrations", "Pricing"] }, { title: "Company", links: ["About Citriot", "Blog", "Contact"] }, { title: "Legal", links: ["Privacy", "Terms", "Security"] }].map(col => (
              <div key={col.title}>
                <div className="text-xs font-bold tracking-widest uppercase text-slate-500 mb-4">{col.title}</div>
                {col.links.map(l => <div key={l} className="text-sm text-slate-400 mb-2.5 cursor-pointer hover:text-white transition-colors">{l}</div>)}
              </div>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ════════════════════════════════════════
   MAIN APP
════════════════════════════════════════ */
const suggestions = ["Monitor live camera feed", "Configure system settings"];

export default function App() {
  const navigate = useNavigate();
  const [chatMode, setChatMode] = useState(false);
  const [placeholderIdx, setPlaceholderIdx] = useState(0);
  const [displayText, setDisplayText] = useState("");
  const [inputVal, setInputVal] = useState("");
  const [openChip, setOpenChip] = useState(null);

  useEffect(() => {
    const citriot = JSON.parse(localStorage.getItem("citriot"));
    const lastLoginType = localStorage.getItem("lastLoginType");

    if (citriot?.auth?.isLoggedIn) {
      if (lastLoginType === "vision") {
        navigate("/login", { replace: true });
        return;
      }

      if (lastLoginType === "configuration") {
        navigate("/loginconfiguration", { replace: true });
        return;
      }
    }
  }, [navigate]);

  const placeholders = [
    "Detect suspicious activity in real-time...",
    "Monitor live AI-powered camera feeds...",
    "Track objects and people intelligently...",
    "Analyse video analytics with deep learning...",
    "Generate smart security alerts instantly...",
    "Review automated incident reports...",
  ];

  const chipOptions = {
    Monitor: ["Staff monitoring", "Canteen surveillance", "Entry / exit control"],
    Analyse: ["Footfall analytics", "Queue time analysis", "Heatmap generation"],
    Configure: ["Camera zones setup", "Alert thresholds", "Role & access control", "Notification routing"],
  };

  useEffect(() => {
    const handler = () => setOpenChip(null);
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (chatMode) return;
    let charIdx = 0, deleting = false;
    let current = placeholders[placeholderIdx];
    const tick = setInterval(() => {
      if (!deleting) {
        charIdx++; setDisplayText(current.slice(0, charIdx));
        if (charIdx === current.length) deleting = true;
      } else {
        charIdx--; setDisplayText(current.slice(0, charIdx));
        if (charIdx === 0) { deleting = false; setPlaceholderIdx(p => (p + 1) % placeholders.length); clearInterval(tick); }
      }
    }, deleting ? 30 : 55);
    return () => clearInterval(tick);
  }, [placeholderIdx, chatMode]);

  const startChat = () => { setInputVal(""); setChatMode(true); };
  const handleSend = () => { setChatMode(true); };
  const handleKeyDown = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } };

  const scrollToChat = () => {
    const el = document.getElementById("opsbot-top");
    if (el) el.scrollIntoView({ behavior: "smooth" });
    setTimeout(() => setChatMode(true), 600);
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,700;0,9..40,800;1,9..40,400&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        body { font-family: 'DM Sans', sans-serif; }

        @keyframes msgIn { from{opacity:0;transform:translateY(7px)} to{opacity:1;transform:translateY(0)} }
        @keyframes slideUp { from{opacity:0;transform:translateY(30px)} to{opacity:1;transform:translateY(0)} }
        @keyframes fadeIn { from{opacity:0} to{opacity:1} }
        @keyframes thinkDot { 0%,80%,100%{transform:scale(.55);opacity:.35} 40%{transform:scale(1);opacity:1} }
        @keyframes cursorBlink { 0%,100%{opacity:1} 50%{opacity:0} }

        @keyframes lpTicker { 0%{transform:translateX(0)} 100%{transform:translateX(-50%)} }
        @keyframes camWalk { 0%{left:20%} 40%{left:62%} 70%{left:42%} 100%{left:20%} }
        @keyframes camHeat { 0%,100%{opacity:.4;transform:scale(1)} 50%{opacity:.8;transform:scale(1.2)} }
        @keyframes camQueue { 0%,100%{width:72%} 50%{width:88%} }
        @keyframes camBob { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-3px)} }
        @keyframes camBox { 0%,100%{opacity:.7;transform:scale(1)} 50%{opacity:1;transform:scale(1.04)} }
        @keyframes camConvey { from{left:100%} to{left:-20px} }
        @keyframes camZone { 0%{top:50%;left:20%} 30%{top:30%;left:60%} 60%{top:65%;left:45%} 100%{top:50%;left:20%} }
        @keyframes camBlink { 0%,100%{opacity:1} 50%{opacity:.3} }

        input[type=range] { -webkit-appearance:none; appearance:none; background:transparent; }
        input[type=range]::-webkit-slider-thumb { -webkit-appearance:none; width:18px; height:18px; border-radius:50%; background:linear-gradient(135deg,#9333ea,#7c3aed); border:2px solid white; box-shadow:0 2px 8px rgba(147,51,234,0.4); cursor:pointer; margin-top:-6px; }
        input[type=range]::-webkit-slider-runnable-track { height:4px; border-radius:2px; background:rgba(209,213,219,0.5); }
      `}</style>

      {/* ── Fixed Nav ── */}
      <nav className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 md:px-12 py-3.5 bg-white/90 backdrop-blur-xl border-b border-purple-100/60">
        <img src="/citriot_logo.webp" alt="Citriot" className="h-8 object-contain" />
        <div className="flex gap-8 items-center">
          <a href="#sectors" className="text-sm text-slate-500 hover:text-slate-800 transition-colors hidden md:block">Sectors</a>
          <a href="#features" className="text-sm text-slate-500 hover:text-slate-800 transition-colors hidden md:block">Features</a>
          <button onClick={scrollToChat} className="bg-gradient-to-r from-purple-600 to-violet-700 text-white border-none rounded-full px-5 py-2 text-sm font-semibold cursor-pointer shadow-lg shadow-purple-200 hover:shadow-xl hover:shadow-purple-300 transition-shadow">
            Get started
          </button>
        </div>
      </nav>

      <div className="pt-14 font-sans">
        <div id="opsbot-top" style={{ background: "linear-gradient(145deg,#F3EFFE 0%,#EDE8FC 30%,#F7F0FF 60%,#FDF8FF 100%)", position: "relative", overflow: "hidden" }}>
          <div className="absolute inset-0 pointer-events-none z-0 overflow-hidden">
            <div style={{ position: "absolute", width: 740, height: 740, background: "radial-gradient(circle,rgba(160,100,255,0.13) 0%,transparent 65%)", top: -210, left: -180, borderRadius: "50%" }} />
            <div style={{ position: "absolute", width: 640, height: 640, background: "radial-gradient(circle,rgba(130,110,255,0.10) 0%,transparent 65%)", bottom: -160, right: -130, borderRadius: "50%" }} />
            <div style={{ position: "absolute", width: 460, height: 460, background: "radial-gradient(circle,rgba(255,160,200,0.10) 0%,transparent 65%)", top: -60, right: "8%", borderRadius: "50%" }} />
            <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(rgba(160,120,220,0.12) 1px,transparent 1px)", backgroundSize: "38px 38px" }} />
          </div>

          <div className="relative z-10 flex flex-col items-center justify-center px-4 py-8 md:py-12" style={{ animation: "fadeIn 0.3s ease" }}>
            <div className="w-full max-w-2xl flex flex-col items-center">

              {!chatMode && (
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 20, padding: "6px 14px", borderRadius: 100, background: "rgba(160,100,255,0.10)", color: "#7c3fc8", border: "1px solid rgba(160,100,255,0.22)", fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase" }}>
                    <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#a060ff", boxShadow: "0 0 7px #a060ff" }} />
                    Citriot AI Platform
                  </div>

                  <h1 style={{ textAlign: "center", fontWeight: 800, color: "#1e1b4b", lineHeight: 1.1, marginBottom: 14, fontSize: "clamp(26px,5vw,46px)", fontFamily: "'DM Sans', sans-serif" }}>
                    AI-Powered Operational Excellence Co-Pilot
                  </h1>
                  <p style={{ textAlign: "center", marginBottom: 8, color: "#7c3fc8", fontSize: "clamp(14px,2vw,18px)", lineHeight: 1.65 }}>
                    From Business Problem to Operational Control Room in Minutes.
                  </p>
                  <p style={{ textAlign: "center", color: "#a78bfa", lineHeight: 1.65, fontSize: "clamp(13px,1.8vw,16px)", marginBottom: 20 }}>
                    Chat now to access your Vision AI dashboard in seconds.
                  </p>

                  <div style={{ display: "flex", gap: 10, marginBottom: 18, width: "100%" }}>
                    <button onClick={() => navigate("/login")} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "10px 12px", borderRadius: 12, fontWeight: 600, fontSize: 13, cursor: "pointer", background: "rgba(255,255,255,0.85)", color: "#d06010", border: "1.5px solid rgba(255,140,50,0.28)" }}>
                      <Eye size={15} /> Vision Sign In
                    </button>
                    <button onClick={() => navigate("/loginconfiguration")} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "10px 12px", borderRadius: 12, fontWeight: 600, fontSize: 13, cursor: "pointer", background: "rgba(255,255,255,0.85)", color: "#009970", border: "1.5px solid rgba(0,200,150,0.28)" }}>
                      <Settings size={15} /> Configuration Sign In
                    </button>
                  </div>

                  <div style={{ width: "100%", borderRadius: 18, background: "rgba(255,255,255,0.82)", border: "1.5px solid rgba(170,130,230,0.22)", boxShadow: "0 8px 40px rgba(120,80,200,0.10),inset 0 1px 0 rgba(255,255,255,1)", backdropFilter: "blur(24px)", overflow: "visible" }}>
                    <div style={{ padding: "14px 16px 10px" }}>
                      <textarea value={inputVal} onChange={e => setInputVal(e.target.value)} onKeyDown={handleKeyDown} placeholder={displayText} rows={3}
                        style={{ width: "100%", background: "transparent", resize: "none", outline: "none", border: "none", fontSize: 15, lineHeight: 1.65, color: "#1e1b4b", caretColor: "#a060ff", fontFamily: "'DM Sans', sans-serif" }} />
                    </div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px 12px", borderTop: "1px solid rgba(170,130,230,0.14)" }}>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", position: "relative" }}>
                        {Object.entries(chipOptions).map(([label, items]) => (
                          <div key={label} style={{ position: "relative" }}>
                            <button onClick={() => setOpenChip(openChip === label ? null : label)} style={{ fontSize: 12, fontWeight: 600, padding: "4px 10px", borderRadius: 8, cursor: "pointer", background: openChip === label ? "rgba(160,100,255,0.14)" : "rgba(160,100,255,0.07)", color: openChip === label ? "#7030e0" : "#a080cc", border: `1px solid ${openChip === label ? "rgba(160,100,255,0.38)" : "rgba(160,100,255,0.16)"}` }}>
                              {label} ▾
                            </button>
                            {openChip === label && (
                              <div style={{ position: "absolute", bottom: "100%", marginBottom: 6, left: 0, zIndex: 50, borderRadius: 12, overflow: "hidden", background: "rgba(255,255,255,0.97)", border: "1.5px solid rgba(160,100,255,0.18)", boxShadow: "0 6px 24px rgba(120,80,200,0.13)", minWidth: 200 }}>
                                <div style={{ padding: "10px 12px 4px", fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#c4a8e8" }}>{label}</div>
                                {items.map(item => (
                                  <div key={item} onClick={() => { setInputVal(item + " "); setOpenChip(null); }} style={{ padding: "8px 12px", fontSize: 13, fontWeight: 500, color: "#1e1b4b", cursor: "pointer" }}>{item}</div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      <button onClick={handleSend} style={{ width: 34, height: 34, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", background: inputVal ? "linear-gradient(135deg,#a060ff,#7030e0)" : "rgba(160,100,255,0.10)", color: inputVal ? "#fff" : "#c4a8e8", border: "none", cursor: inputVal ? "pointer" : "default", boxShadow: inputVal ? "0 4px 16px rgba(160,80,255,0.38)" : "none" }}>
                        <Send size={17} />
                      </button>
                    </div>
                  </div>

                  <button onClick={handleSend} style={{ width: "100%", marginTop: 10, padding: "14px", borderRadius: 18, background: "linear-gradient(135deg,#a060ff,#7030e0)", color: "#fff", border: "none", fontSize: 15, fontWeight: 800, cursor: "pointer", boxShadow: "0 6px 22px rgba(140,60,255,0.35)" }}>
                    ⚡ Start OpsBot Setup →
                  </button>

                  <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 8, marginTop: 16 }}>
                    {suggestions.map(s => (
                      <button key={s} onClick={startChat} style={{ fontSize: 12, padding: "6px 16px", borderRadius: 100, fontWeight: 500, cursor: "pointer", background: "rgba(255,255,255,0.75)", color: "#a080cc", border: "1px solid rgba(170,130,230,0.22)" }}>{s}</button>
                    ))}
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 24, fontSize: 11, fontWeight: 500, color: "#c4b5fd", flexWrap: "wrap", justifyContent: "center" }}>
                    <span>✦ Built for AI</span><span>·</span><span>✦ Trusted Devices</span><span>·</span><span>✦ One Seamless Workflow</span>
                  </div>
                </>
              )}

              {chatMode && (
                <div style={{ width: "100%", borderRadius: 18, overflow: "hidden", height: 680, border: "1.5px solid rgba(170,130,230,0.22)", boxShadow: "0 8px 40px rgba(120,80,200,0.10)", background: "rgba(255,255,255,0.82)", backdropFilter: "blur(24px)", animation: "slideUp 0.35s cubic-bezier(0.22,1,0.36,1)" }}>
                  <InlineChatSection navigate={navigate} onCollapse={() => setChatMode(false)} />
                </div>
              )}
            </div>
          </div>
        </div>

        {!chatMode && (
          <>
            <LandingTicker />
            <CameraGrid />
            <HowItWorks />
            <section id="features"><Features /></section>
            <ModelTraining />
            <section id="sectors"><Sectors /></section>
            <Footer />
          </>
        )}
      </div>
    </>
  );
}